// apps/spa/src/hooks/usePostNow.ts
// Discriminated-response handler for POST /api/reminders/:id/post-now.
//
// Server response shapes (locked in 02-02):
//   200 { success: true,  messageId }                      -> green success toast
//   200 { success: false, retryAfter, error: 'rate_limited' } -> amber warning toast
//                                                              with 'Retry now' action
//   502 { error: { code: 'dispatch_failed', message } }    -> red error toast
//
// The api wrapper throws ApiError on non-2xx, so the 502 path lands in onError;
// the two 200 paths land in onSuccess and are discriminated by `result.success`.
//
// Retry now action wiring: we expose a `retryRef` so the toast's onClick can
// invoke the latest mutate function (avoids stale closures over `mutate`).

import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export type PostNowSuccess = { success: true; messageId: string };
export type PostNowRateLimited = {
  success: false;
  retryAfter: number;
  error: 'rate_limited';
};
export type PostNowResult = PostNowSuccess | PostNowRateLimited;

export type PostNowVars = { id: string; channelName: string };

export function usePostNow() {
  // Stable ref to the latest mutate fn so the Retry-now toast action does not
  // close over a stale reference after re-renders.
  const mutateRef = useRef<((vars: PostNowVars) => void) | null>(null);

  const mutation = useMutation({
    mutationFn: (vars: PostNowVars) =>
      api<PostNowResult>(`/api/reminders/${vars.id}/post-now`, {
        method: 'POST',
      }),
    onSuccess: (result, vars) => {
      if (result.success) {
        toast.success(`Posted to #${vars.channelName}`);
        return;
      }
      // 200 with success: false — rate limited
      toast.warning(`Discord rate-limited — retry in ${result.retryAfter}s.`, {
        action: {
          label: 'Retry now',
          onClick: () => mutateRef.current?.(vars),
        },
      });
    },
    onError: () => {
      // 502 dispatch_failed (or any other non-2xx) lands here via ApiError.
      toast.error(
        "Couldn't post to Discord. Check the channel and try again.",
      );
    },
  });

  // Always point the ref at the freshest mutate.
  mutateRef.current = mutation.mutate;

  return mutation;
}
